"use client"

import { clx, Heading } from "@medusajs/ui"
import { ChevronDownMini, ChevronUpMini, CheckCircleSolid } from "@medusajs/icons"

type CardProps = {
  title: string
  subtitle?: string
  isActive: boolean
  isCompleted?: boolean
  children: React.ReactNode
  onEdit?: () => void
  collapsible?: boolean
}

/**
 * Card component for express checkout steps
 * Provides a collapsible card with title, active/completed states
 */
const Card = ({
  title,
  subtitle,
  isActive,
  isCompleted = false,
  children,
  onEdit,
  collapsible = true,
}: CardProps) => {
  return (
    <div
      className={clx(
        "bg-white rounded-lg py-4 px-6 w-full",
        "flex gap-4 flex-col shadow-elevation-card-rest transition-all duration-200",
        {
          "ring-2 ring-neutral-900": isActive,
          "opacity-60": !isActive && !isCompleted,
        }
      )}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Heading level="h2" className="text-lg font-semibold">
              {title}
            </Heading>
            {isCompleted && !isActive && (
              <CheckCircleSolid className="text-green-500 w-5 h-5" />
            )}
          </div>
          {subtitle && !isActive && (
            <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isCompleted && !isActive && onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-neutral-600 hover:text-neutral-900 underline"
            >
              Uredi
            </button>
          )}
          {collapsible && (
            <div className="text-neutral-400">
              {isActive ? (
                <ChevronUpMini className="w-5 h-5" />
              ) : (
                <ChevronDownMini className="w-5 h-5" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Content */}
      {isActive && (
        <div className="flex flex-col gap-4">
          <hr className="border-neutral-200" />
          {children}
        </div>
      )}
    </div>
  )
}

export default Card

